import { z, type ZodType } from 'zod';

export type ComponentCategory =
  | 'primitive'
  | 'data'
  | 'form'
  | 'layout'
  | 'logistics'
  | 'pattern'
  | 'studio';

export interface SlotDescriptor {
  name: string;
  description?: string;
  /** Component names allowed in the slot; empty = any. */
  accepts?: string[];
}

export interface DataBindingCapabilities {
  /** Which prop names can be bound to a data source (api/query/context). */
  bindableProps: string[];
  /** The component accepts a page of rows (`content`, `page`, `size`, `totalElements`). */
  acceptsPage?: boolean;
  /** The component emits state changes (sort/filter/page) usable for server-side data ops. */
  emitsState?: boolean;
  /** The component acts as a form field (value/onChange contract). */
  field?: boolean;
  /** Accepts a SemanticQuery result ({ columns, rows }). */
  acceptsSemanticResult?: boolean;
}

export interface ComponentDescriptor<P = unknown> {
  name: string;
  category: ComponentCategory;
  description: string;
  /** zod schema of the props that Studio may set in metadata. */
  props: ZodType<P>;
  slots: SlotDescriptor[];
  dataBinding: DataBindingCapabilities;
  /** Optional preview props for the playground / Studio palette. */
  examples?: Array<{ name: string; props: Partial<P> }>;
  /** Tags for palette search. */
  tags?: string[];
}

export function defineDescriptor<P>(d: ComponentDescriptor<P>): ComponentDescriptor<P> {
  return d;
}

export interface SerializedComponentDescriptor {
  name: string;
  category: ComponentCategory;
  description: string;
  propsSchema: Record<string, unknown>;
  slots: SlotDescriptor[];
  dataBinding: DataBindingCapabilities;
  tags: string[];
  examples: Array<{ name: string; props: Record<string, unknown> }>;
}

/** Converts a descriptor to a JSON-serializable form (props zod schema → JSON Schema). */
export function serializeDescriptor(d: ComponentDescriptor<unknown>): SerializedComponentDescriptor {
  let propsSchema: Record<string, unknown>;
  try {
    propsSchema = z.toJSONSchema(d.props as z.ZodType, { unrepresentable: 'any', io: 'input' }) as Record<string, unknown>;
  } catch {
    propsSchema = { type: 'object' };
  }
  return {
    name: d.name,
    category: d.category,
    description: d.description,
    propsSchema,
    slots: d.slots,
    dataBinding: d.dataBinding,
    tags: d.tags ?? [],
    examples: (d.examples ?? []).map((e) => ({ name: e.name, props: e.props as Record<string, unknown> })),
  };
}

/** Common prop fragments reused by descriptors. */
export const commonProps = {
  className: z.string().optional().describe('Extra CSS classes'),
  id: z.string().optional(),
  testId: z.string().optional(),
};
